import { useBaseToast } from '@/src/hooks/useBaseToast'
import ErrorBoundary from '@/src/shared/ErrorBoundary'
import { Box, Portal } from '@chakra-ui/react'
import DataEditor, {
  type DataEditorRef,
  type GridCell,
  GridCellKind,
  type GridColumn,
  type GridMouseEventArgs,
  type GridSelection,
  type Item,
  type Rectangle,
  type TextCell,
} from '@glideapps/glide-data-grid'
import { type ComponentProps, type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MAX_PASTE_ROWS, formatValueForDisplay, isPasteTooLarge, normalizeNumber } from './EditableDataGrid.utils'

const VERTICAL_SCROLLBAR_WIDTH = 16
const MIN_FITTED_COLUMN_WIDTH = 90

export interface EditableDataGridColumn {
  title: string
  id: string
  width?: number
  grow?: number
  align?: 'left' | 'right' | 'center'
  icon?: string
  readonly?: boolean
  format?: 'number' | 'text'
}

export interface EditableDataGridRow {
  id: string
  cells: (string | number)[]
}

export interface EditableDataGridCellChange {
  rowId: string
  colId: string
  oldValue: string | number
  newValue: string | number
}

/** A tooltip label, or a label that opens only after the pointer rests on the item for `openDelay` ms. */
export type EditableDataGridTooltip = ReactNode | { label: ReactNode; openDelay: number }

const isDelayedTooltip = (tooltip: EditableDataGridTooltip): tooltip is { label: ReactNode; openDelay: number } =>
  typeof tooltip === 'object' && tooltip !== null && 'openDelay' in tooltip

export interface EditableDataGridProps {
  columns: EditableDataGridColumn[]
  data: EditableDataGridRow[]
  initialData?: EditableDataGridRow[]
  onDataChange?: (data: EditableDataGridRow[]) => void
  height?: number | string
  width?: number | string
  rowHeight?: number
  headerHeight?: number
  highlightChangedCells?: boolean
  onCellsChanged?: (changes: EditableDataGridCellChange[]) => void
  minColumnWidth?: number
  maxColumnWidth?: number
  isCellReadonly?: (rowId: string, colId: string, rowIndex: number, colIndex: number) => boolean
  getCellThemeOverride?: (
    rowId: string,
    colId: string,
    rowIndex: number,
    colIndex: number
  ) => { bgCell?: string; textDark?: string; accentColor?: string } | undefined
  getCellTooltip?: (rowId: string, colId: string, rowIndex: number, colIndex: number) => EditableDataGridTooltip
  getHeaderTooltip?: (colId: string, colIndex: number) => EditableDataGridTooltip
  getCellClickDetail?: (rowId: string, colId: string, rowIndex: number, colIndex: number) => ReactNode
  onHeaderClicked?: (col: number) => void
  drawHeader?: ComponentProps<typeof DataEditor>['drawHeader']
  drawCell?: ComponentProps<typeof DataEditor>['drawCell']
  headerIcons?: ComponentProps<typeof DataEditor>['headerIcons']
  theme?: ComponentProps<typeof DataEditor>['theme']
  getRowThemeOverride?: ComponentProps<typeof DataEditor>['getRowThemeOverride']
}

export const EditableDataGrid = ({
  columns,
  data,
  initialData,
  onDataChange,
  height,
  width,
  rowHeight = 34,
  headerHeight = 36,
  highlightChangedCells = false,
  onCellsChanged,
  minColumnWidth = 150,
  maxColumnWidth = 500,
  isCellReadonly,
  getCellThemeOverride,
  getCellTooltip,
  getHeaderTooltip,
  getCellClickDetail,
  onHeaderClicked,
  drawHeader,
  drawCell,
  headerIcons,
  theme,
  getRowThemeOverride,
}: EditableDataGridProps) => {
  const gridRef = useRef<DataEditorRef>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [localData, setLocalData] = useState<EditableDataGridRow[]>(data)
  const [containerWidth, setContainerWidth] = useState<number>()
  const [hoveredTooltip, setHoveredTooltip] = useState<{ label: ReactNode; bounds: Rectangle } | null>(null)
  const tooltipTimer = useRef<ReturnType<typeof setTimeout>>()
  const [expandedCell, setExpandedCell] = useState<{ label: ReactNode; title: string; bounds: Rectangle } | null>(null)
  const expandedCellRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const toast = useBaseToast()

  const hideTooltip = () => {
    clearTimeout(tooltipTimer.current)
    setHoveredTooltip(null)
  }

  useEffect(() => () => clearTimeout(tooltipTimer.current), [])

  useLayoutEffect(() => {
    setLocalData(data)
    hideTooltip()
    setExpandedCell(null)
  }, [data])

  useLayoutEffect(() => {
    if (!expandedCell) return
    const dismissOnOutsideClick = (event: PointerEvent) => {
      if (!expandedCellRef.current?.contains(event.target as Node)) setExpandedCell(null)
    }
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpandedCell(null)
    }
    window.addEventListener('pointerdown', dismissOnOutsideClick)
    window.addEventListener('keydown', dismissOnEscape)
    return () => {
      window.removeEventListener('pointerdown', dismissOnOutsideClick)
      window.removeEventListener('keydown', dismissOnEscape)
    }
  }, [expandedCell])

  const rejectOversizedPasteRef = useRef<(event: ClipboardEvent) => void>()
  rejectOversizedPasteRef.current = (event: ClipboardEvent) => {
    const activeElement = document.activeElement
    if (!activeElement || wrapperRef.current?.contains(activeElement) !== true) return
    if (!isPasteTooLarge(event.clipboardData)) return

    event.preventDefault()
    event.stopImmediatePropagation()

    toast({
      title: t('data_management.paste_too_large_title'),
      description: t('data_management.paste_too_large_description', { maxRows: MAX_PASTE_ROWS }),
      status: 'error',
    })
  }

  useLayoutEffect(() => {
    const onWindowPaste = (event: ClipboardEvent) => rejectOversizedPasteRef.current?.(event)
    window.addEventListener('paste', onWindowPaste, { capture: true })
    return () => window.removeEventListener('paste', onWindowPaste, { capture: true })
  }, [])

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    const observer = new ResizeObserver(([entry]) => setContainerWidth(entry.contentRect.width))
    observer.observe(wrapper)
    return () => observer.disconnect()
  }, [])

  const contentHeight = localData.length * rowHeight + headerHeight + 2

  const calculatedHeight = useMemo(() => {
    if (height !== undefined) return height
    return Math.min(contentHeight, window.innerHeight * 0.7)
  }, [height, contentHeight])

  const hasVerticalScrollbar = typeof calculatedHeight === 'number' && contentHeight > calculatedHeight
  const scrollbarAllowance = hasVerticalScrollbar ? VERTICAL_SCROLLBAR_WIDTH : 0
  const availableWidth = containerWidth === undefined || containerWidth <= 0 ? undefined : containerWidth - scrollbarAllowance

  const columnWidths = useMemo(() => {
    const preferredWidths = columns.map(col => col.width ?? minColumnWidth)
    const preferredTotal = preferredWidths.reduce((sum, width) => sum + width, 0)

    if (availableWidth === undefined || availableWidth >= preferredTotal) return preferredWidths

    const scale = availableWidth / preferredTotal
    return preferredWidths.map(width => Math.max(Math.floor(width * scale), Math.min(MIN_FITTED_COLUMN_WIDTH, width)))
  }, [columns, minColumnWidth, availableWidth])

  const gridColumns: GridColumn[] = useMemo(
    () =>
      columns.map((col, index) => ({
        title: col.title,
        id: col.id,
        width: columnWidths[index],
        grow: col.grow,
        icon: col.icon,
      })),

    [columns, columnWidths]
  )

  const calculatedWidth = useMemo(() => {
    const fittedWidth = columnWidths.reduce((sum, width) => sum + width, 0) + scrollbarAllowance

    if (containerWidth === undefined || containerWidth <= 0) return fittedWidth
    return Math.min(fittedWidth, containerWidth)
  }, [columnWidths, scrollbarAllowance, containerWidth])

  const getCellContent = ([col, row]: Item): GridCell => {
    const rowData = localData[row]
    if (!rowData || col >= columns.length) {
      return {
        kind: GridCellKind.Text,
        data: '',
        displayData: '',
        readonly: true,
        allowOverlay: false,
      }
    }

    const column = columns[col]
    const cellValue = rowData.cells[col]
    const formattedValue = formatValueForDisplay(cellValue, column.format)
    const isChanged = highlightChangedCells && initialData?.[row] && initialData[row].cells[col] !== cellValue

    const cellReadonly = isCellReadonly ? isCellReadonly(rowData.id, column.id, row, col) : column.readonly || false

    const customThemeOverride = getCellThemeOverride ? getCellThemeOverride(rowData.id, column.id, row, col) : undefined

    const themeOverride =
      customThemeOverride ||
      (isChanged
        ? {
            bgCell: '#f0fdf4',
            accentColor: '#008236',
          }
        : undefined)

    return {
      kind: GridCellKind.Text,
      data: String(cellValue ?? ''),
      displayData: formattedValue,
      readonly: cellReadonly,
      allowOverlay: !cellReadonly,
      contentAlign: column.align ?? 'right',
      themeOverride,
    }
  }

  const onItemHovered = (args: GridMouseEventArgs) => {
    if (expandedCell || (args.kind !== 'cell' && args.kind !== 'header')) {
      hideTooltip()
      return
    }
    const [colIndex, rowIndex] = args.location
    const column = columns[colIndex]
    const row = localData[rowIndex]
    const tooltip =
      args.kind === 'header'
        ? column && getHeaderTooltip?.(column.id, colIndex)
        : column && row && getCellTooltip?.(row.id, column.id, rowIndex, colIndex)
    hideTooltip()
    const label = isDelayedTooltip(tooltip) ? tooltip.label : tooltip
    if (label === undefined || label === null || label === false) return
    const shownTooltip = { label, bounds: args.bounds }
    if (isDelayedTooltip(tooltip)) {
      tooltipTimer.current = setTimeout(() => setHoveredTooltip(shownTooltip), tooltip.openDelay)
    } else {
      setHoveredTooltip(shownTooltip)
    }
  }

  const onCellClicked: NonNullable<ComponentProps<typeof DataEditor>['onCellClicked']> = ([colIndex, rowIndex], event) => {
    if (!getCellClickDetail) return
    const row = localData[rowIndex]
    const column = columns[colIndex]
    const label = row && column ? getCellClickDetail(row.id, column.id, rowIndex, colIndex) : undefined
    hideTooltip()
    setExpandedCell(label === undefined || label === null ? null : { label, title: column?.title ?? '', bounds: event.bounds })
  }

  const onCellEdited = ([col, row]: Item, newValue: GridCell) => {
    const column = columns[col]
    const rowData = localData[row]
    const cellReadonly = isCellReadonly ? isCellReadonly(rowData.id, column.id, row, col) : column.readonly || false

    if (cellReadonly) return

    const cellValue = (newValue as TextCell).data ?? ''
    const cleanedValue = column.format === 'number' ? normalizeNumber(cellValue) : cellValue

    setLocalData(prev => {
      const updated = prev.map((r, idx) => (idx === row ? { ...r, cells: [...r.cells] } : r))
      const oldValue = updated[row].cells[col]
      updated[row].cells[col] = cleanedValue

      if (onCellsChanged) {
        onCellsChanged([
          {
            rowId: updated[row].id,
            colId: column.id,
            oldValue,
            newValue: cleanedValue,
          },
        ])
      }
      if (onDataChange) {
        onDataChange(updated)
      }
      return updated
    })
  }

  const onPaste = (target: Item, values: readonly (readonly string[])[]) => {
    const [startCol, startRow] = target

    setLocalData(prev => {
      const updated = prev.map(row => ({ ...row, cells: [...row.cells] }))
      let modified = false
      const changes: EditableDataGridCellChange[] = []

      values.forEach((row, rowOffset) => {
        const targetRow = startRow + rowOffset
        if (targetRow < updated.length) {
          row.forEach((cellValue, colOffset) => {
            const targetCol = startCol + colOffset
            if (targetCol < columns.length) {
              const column = columns[targetCol]
              const rowData = updated[targetRow]
              const cellReadonly = isCellReadonly ? isCellReadonly(rowData.id, column.id, targetRow, targetCol) : column.readonly || false

              if (!cellReadonly) {
                const cleanedValue = column.format === 'number' ? normalizeNumber(cellValue || '') : cellValue
                const oldValue = updated[targetRow].cells[targetCol]
                updated[targetRow].cells[targetCol] = cleanedValue
                modified = true

                changes.push({
                  rowId: updated[targetRow].id,
                  colId: column.id,
                  oldValue,
                  newValue: cleanedValue,
                })
              }
            }
          })
        }
      })

      if (modified) {
        if (onCellsChanged && changes.length > 0) {
          onCellsChanged(changes)
        }
        if (onDataChange) {
          onDataChange(updated)
        }
      }
      return updated
    })

    return false
  }

  const getCellsForSelection = (selection: Rectangle): GridCell[][] => {
    const results: GridCell[][] = []
    for (let row = selection.y; row < selection.y + selection.height; row++) {
      const rowData: GridCell[] = []
      for (let col = selection.x; col < selection.x + selection.width; col++) {
        rowData.push(getCellContent([col, row]))
      }
      results.push(rowData)
    }

    return results
  }

  const onDelete = (selection: GridSelection): boolean | GridSelection => {
    if (!selection.current) return false
    const { range } = selection.current

    setLocalData(prev => {
      const updated = prev.map(row => ({ ...row, cells: [...row.cells] }))
      let modified = false
      const changes: EditableDataGridCellChange[] = []

      for (let row = range.y; row < range.y + range.height; row++) {
        if (row >= 0 && row < updated.length) {
          for (let col = range.x; col < range.x + range.width; col++) {
            if (col >= 0 && col < columns.length) {
              const column = columns[col]
              const rowData = updated[row]
              const cellReadonly = isCellReadonly ? isCellReadonly(rowData.id, column.id, row, col) : column.readonly || false

              if (!cellReadonly) {
                const oldValue = updated[row].cells[col]
                updated[row].cells[col] = ''
                modified = true

                changes.push({
                  rowId: updated[row].id,
                  colId: columns[col].id,
                  oldValue,
                  newValue: '',
                })
              }
            }
          }
        }
      }

      if (modified) {
        if (onCellsChanged && changes.length > 0) {
          onCellsChanged(changes)
        }
        if (onDataChange) {
          onDataChange(updated)
        }
      }
      return updated
    })

    return true
  }

  return (
    <ErrorBoundary boundaryType='content'>
      <Box ref={wrapperRef} width={width ?? calculatedWidth} height={calculatedHeight} onMouseLeave={() => hideTooltip()}>
        <DataEditor
          ref={gridRef}
          columns={gridColumns}
          rows={localData.length}
          getCellContent={getCellContent}
          onCellEdited={onCellEdited}
          onPaste={onPaste}
          getCellsForSelection={getCellsForSelection}
          spanRangeBehavior='allowPartial'
          onDelete={onDelete}
          onHeaderClicked={onHeaderClicked}
          drawHeader={drawHeader}
          drawCell={drawCell}
          headerIcons={headerIcons}
          theme={theme}
          getRowThemeOverride={getRowThemeOverride}
          onItemHovered={getCellTooltip || getHeaderTooltip ? onItemHovered : undefined}
          onCellClicked={getCellClickDetail ? onCellClicked : undefined}
          onVisibleRegionChanged={
            getCellTooltip || getCellClickDetail
              ? () => {
                  hideTooltip()
                  setExpandedCell(null)
                }
              : undefined
          }
          height={calculatedHeight}
          width={width ?? calculatedWidth}
          rowHeight={rowHeight}
          headerHeight={headerHeight}
          minColumnWidth={Math.min(minColumnWidth, ...columnWidths)}
          maxColumnWidth={maxColumnWidth}
        />
        {hoveredTooltip && !expandedCell && (
          <Portal>
            <Box
              role='tooltip'
              position='fixed'
              // Near the right edge, align with the cell's right side so short tooltips stay next to their cell.
              left={hoveredTooltip.bounds.x > window.innerWidth - 656 ? undefined : `${Math.max(8, hoveredTooltip.bounds.x)}px`}
              right={
                hoveredTooltip.bounds.x > window.innerWidth - 656
                  ? `${Math.max(8, window.innerWidth - hoveredTooltip.bounds.x - hoveredTooltip.bounds.width)}px`
                  : undefined
              }
              top={`${hoveredTooltip.bounds.y > 210 ? hoveredTooltip.bounds.y - 8 : hoveredTooltip.bounds.y + hoveredTooltip.bounds.height + 8}px`}
              transform={hoveredTooltip.bounds.y > 210 ? 'translateY(-100%)' : undefined}
              zIndex='tooltip'
              maxW='min(640px, calc(100vw - 16px))'
              maxH='min(60vh, 420px)'
              overflowY='auto'
              p={3}
              bg='white'
              color='gray.800'
              borderWidth='1px'
              borderColor='gray.200'
              borderRadius='base'
              boxShadow='md'
              fontSize='sm'
              pointerEvents='none'
            >
              {hoveredTooltip.label}
            </Box>
          </Portal>
        )}
        {expandedCell && (
          <Portal>
            <Box
              ref={expandedCellRef}
              role='dialog'
              aria-label={expandedCell.title}
              position='fixed'
              left={`${Math.max(8, Math.min(expandedCell.bounds.x, window.innerWidth - 528))}px`}
              top={
                expandedCell.bounds.y <= window.innerHeight / 2 ? `${expandedCell.bounds.y + expandedCell.bounds.height + 6}px` : undefined
              }
              bottom={expandedCell.bounds.y > window.innerHeight / 2 ? `${window.innerHeight - expandedCell.bounds.y + 6}px` : undefined}
              zIndex='tooltip'
              w='max-content'
              maxW='min(520px, calc(100vw - 16px))'
              maxH={`${Math.max(
                80,
                Math.min(
                  360,
                  expandedCell.bounds.y <= window.innerHeight / 2
                    ? window.innerHeight - expandedCell.bounds.y - expandedCell.bounds.height - 22
                    : expandedCell.bounds.y - 22
                )
              )}px`}
              overflowY='auto'
              p={3}
              bg='white'
              color='gray.800'
              borderWidth='1px'
              borderColor='gray.200'
              borderRadius='base'
              boxShadow='md'
              fontSize='sm'
              overflowWrap='anywhere'
            >
              {expandedCell.label}
            </Box>
          </Portal>
        )}
      </Box>
    </ErrorBoundary>
  )
}
